import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import type { SagaWorkflowGraphDto } from '@/dtos/SagaWorkflow'
import type { ActiveInsuranceSaga } from '@/hooks/useInsuranceSagas'
import { useGetActiveSagas } from '@/hooks/useInsuranceSagas'
import { AppSettings } from '../appSettings/AppSettings'
import { useAuth } from './AuthContext'

const SAGA_WORKFLOW_HUB_PATH = 'hub/saga-workflow'

type InsuranceSagaContextProps = {
  /** Ne-terminální sagy pojištění (zdroj pro saga-vázané akce, např. zrušení čekajícího storna). */
  activeSagas: ActiveInsuranceSaga[]
  /** Odběr událostí sag tohoto pojištění; vrací unsubscribe. */
  subscribe: (listener: (graph: SagaWorkflowGraphDto) => void) => () => void
  refetchSagas: () => void
}

const InsuranceSagaContext = createContext<InsuranceSagaContextProps>({
  activeSagas: [],
  subscribe: () => () => { },
  refetchSagas: () => { },
})

/**
 * Insurance-scoped saga stream: fetches the insurance's non-terminal sagas, subscribes their workflow roots on
 * the saga-workflow hub and republishes every push to context subscribers. Consumers react by refetching the
 * data that shares the sagas' lifecycle (typically GetInsurance → policyStatus). Active only for users with
 * enableSagaWorkflows (IsLogged; VWFS) — otherwise renders an inert context: no fetch, no hub connection.
 */
export const InsuranceSagaProvider = ({
  insuranceId,
  children,
}: {
  insuranceId: number | undefined
  children: React.ReactNode
}) => {
  const { user } = useAuth()
  const enabled = user?.enableSagaWorkflows === true

  const { data, refetch } = useGetActiveSagas(insuranceId, enabled)
  const activeSagas = data?.sagas ?? []

  const listenersRef = useRef(new Set<(graph: SagaWorkflowGraphDto) => void>())

  const subscribe = useCallback((listener: (graph: SagaWorkflowGraphDto) => void) => {
    listenersRef.current.add(listener)
    return () => listenersRef.current.delete(listener)
  }, [])

  const emit = (graph: SagaWorkflowGraphDto) => {
    for (const listener of listenersRef.current) listener(graph)
  }
  const emitRef = useRef(emit)
  emitRef.current = emit

  const refetchRef = useRef(refetch)
  refetchRef.current = refetch

  // One connection per distinct root set; reopen only when the set actually changes (join for a stable dep).
  const rootIds = [...new Set(activeSagas.map(s => s.rootSagaId))].sort()
  const rootsKey = rootIds.join('|')

  useEffect(() => {
    if (!enabled || rootIds.length === 0) return
    let unmounted = false

    const url = `${AppSettings.baseUrl}/${SAGA_WORKFLOW_HUB_PATH}`
    const conn = new HubConnectionBuilder()
      .configureLogging(LogLevel.Warning)
      .withUrl(url)
      .withAutomaticReconnect()
      .build()

    const subscribed = new Set(rootIds)

    conn.on('SagaWorkflowUpdated', (g: SagaWorkflowGraphDto) => {
      if (!subscribed.has(g.rootSagaId)) return // stale push mid-unsubscribe for a different root
      emitRef.current(g)
      if (g.isTerminal) {
        subscribed.delete(g.rootSagaId)
        conn.invoke('UnsubscribeSaga', g.rootSagaId).catch(() => { /* connection may be closing */ })
      }
    })

    const subscribeAll = () => Promise.all(
      [...subscribed].map(rootId => conn.invoke('SubscribeSaga', rootId).catch(() => { /* connection race — ignore */ })),
    )

    // Reconnect drops server-side group membership → re-subscribe and refetch the authoritative saga list; the
    // synthetic refetch also covers pushes missed during the gap (consumers read fresh state on the next emit).
    conn.onreconnected(async () => {
      await subscribeAll()
      refetchRef.current()
    })

    const start = async () => {
      try {
        await conn.start()
        if (unmounted) {
          conn.stop()
          return
        }
        await subscribeAll()
      } catch {
        // Connection failed (offline / not logged in) — no live updates; data converges on the next refetch.
      }
    }
    start()

    return () => {
      unmounted = true
      for (const rootId of subscribed)
        conn.invoke('UnsubscribeSaga', rootId).catch(() => { /* connection may already be closing */ })
      conn.stop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, rootsKey])

  return (
    <InsuranceSagaContext.Provider value={{ activeSagas, subscribe, refetchSagas: refetch }}>
      {children}
    </InsuranceSagaContext.Provider>
  )
}

export const useInsuranceSagas = () => useContext(InsuranceSagaContext)

/** Zavolá listener při každé události sag pojištění (push ze SagaWorkflowHub). */
export const useInsuranceSagaEvents = (listener: (graph: SagaWorkflowGraphDto) => void) => {
  const { subscribe } = useInsuranceSagas()
  const listenerRef = useRef(listener)
  listenerRef.current = listener

  useEffect(() => subscribe(g => listenerRef.current(g)), [subscribe])
}
